import { Button, Divider, Flex, Space } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { TransactionTypeFilter } from '@features/bankTransactions/enums/transactionTypeFilter'

import { LinkableTransactionsFilters } from '../../../..'
import { useDebouncedCallback } from '../../../../../../../../../../../../../hooks/useDebouncedCallback'
import { AmountRangeFilter } from '../../../../../AmountRangeFilter/AmountRangeFilter'
import { SideFilter } from '../../../../../SideFilter'
import * as Styled from './styles'

const DEBOUNCE_DELAY = 500

interface TransactionsListFiltersPanelProps {
  filters: LinkableTransactionsFilters
  currency?: string
  onAmountRangeChange?: (minAmount?: number, maxAmount?: number) => void
  onTransactionSideChange?: (transactionSideFilter?: TransactionTypeFilter) => void
  resetFilters: () => void
}

export const TransactionsListFiltersPanel = ({
  filters,
  onAmountRangeChange,
  onTransactionSideChange,
  resetFilters,
  currency,
}: TransactionsListFiltersPanelProps): ReactElement => {
  const { t } = useTranslation()

  const handleClearAll = useCallback(() => {
    resetFilters()
  }, [resetFilters])

  const handleTransactionSideChange = useCallback(
    (value: TransactionTypeFilter) => {
      onTransactionSideChange?.(value)
    },
    [onTransactionSideChange],
  )

  const handleMinAmountChange = useDebouncedCallback((value: number) => {
    onAmountRangeChange?.(value || undefined, filters.maxAmount)
  }, DEBOUNCE_DELAY)

  const handleMaxAmountChange = useDebouncedCallback((value: number) => {
    onAmountRangeChange?.(filters.minAmount, value || undefined)
  }, DEBOUNCE_DELAY)

  return (
    <Styled.Panel>
      <SideFilter value={filters.transactionSideFilter} onChange={handleTransactionSideChange} />
      <Space size="s8" />
      <AmountRangeFilter
        minAmount={filters.minAmount}
        maxAmount={filters.maxAmount}
        currency={currency}
        onMinChange={handleMinAmountChange}
        onMaxChange={handleMaxAmountChange}
      />
      <Styled.FullWidthDivider>
        <Divider />
      </Styled.FullWidthDivider>
      <Flex justifyContent="flex-start">
        <Button variant="text" size="sm" icon="x" onClick={handleClearAll}>
          {t('bank_transactions.filter.clear_all')}
        </Button>
      </Flex>
    </Styled.Panel>
  )
}
